import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { useSmoothScroll } from "@/hooks/useSmoothScroll";
import { CurrencyProvider } from "@/context/CurrencyContext";
import { CartProvider } from "@/context/CartContext";
import { PricesProvider } from "@/context/PricesContext";
import { SchemasProvider } from "@/context/SchemasContext";
import { AdminAuthProvider } from "@/context/AdminAuthContext";
import { CustomerAuthProvider } from "@/context/CustomerAuthContext";
import { HomeContentProvider } from "@/context/HomeContentContext";
import { Navbar } from "@/components/Navbar";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import { Hero } from "@/components/Hero";
import { CategoryIconGrid } from "@/components/CategoryIconGrid";
import { OurJourney } from "@/components/OurJourney";
import { Seo, SeoVerification } from "@/components/Seo";
import { Footer } from "@/components/Footer";
import { HomeLuxe } from "@/pages/HomeLuxe";
import HomeReference from "@/pages/HomeReference";
import { CartDrawer } from "@/components/CartDrawer";
import { LiveChat } from "@/components/LiveChat";
import { useLocation } from "react-router-dom";
import { ShopPage } from "@/pages/ShopPage";
import { ProductPage } from "@/pages/ProductPage";
import { CheckoutPage } from "@/pages/CheckoutPage";
import { PaymentSuccessPage, PaymentCancelPage } from "@/pages/PaymentStatusPages";
import { AdminLoginPage, RequireAdmin } from "@/pages/AdminLoginPage";
import { AdminDashboardPage } from "@/pages/AdminDashboardPage";
import { AuthPage } from "@/pages/AuthPage";
import { AuthCallback } from "@/pages/AuthCallback";
import { AccountPage } from "@/pages/AccountPage";
import AboutPage from "@/pages/AboutPage";
import SpneDesignPage from "@/pages/SpneDesignPage";
import TrackOrderPage from "@/pages/TrackOrderPage";

// Hide LiveChat on the home page (user wants a clean, popup-free home) and on admin routes
const LiveChatGuard = () => {
  const loc = useLocation();
  const p = loc.pathname;
  if (p === "/" || p.startsWith("/admin") || p.startsWith("/account") || p.startsWith("/auth") || p.startsWith("/track-order") || p.startsWith("/checkout")) return null;
  return <LiveChat />;
};

const Home = () => {
  useSmoothScroll();
  return <HomeReference />;
};

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <SeoVerification />
        <BrowserRouter>
          <CurrencyProvider>
            <PricesProvider>
              <SchemasProvider>
                <HomeContentProvider>
                  <AdminAuthProvider>
                    <CustomerAuthProvider>
                      <CartProvider>
                        <Routes>
                          <Route path="/" element={<Home />} />
                          <Route path="/about" element={<AboutPage />} />
                          <Route path="/spne-design" element={<SpneDesignPage />} />
                          <Route path="/shop" element={<ShopPage />} />
                          <Route path="/product/:slug" element={<ProductPage />} />
                          <Route path="/checkout" element={<CheckoutPage />} />
                          <Route path="/payment/success" element={<PaymentSuccessPage />} />
                          <Route path="/payment/cancel" element={<PaymentCancelPage />} />
                          <Route path="/track-order" element={<TrackOrderPage />} />
                          <Route path="/account/login" element={<AuthPage mode="login" />} />
                          <Route path="/account/signup" element={<AuthPage mode="signup" />} />
                          <Route path="/account" element={<AccountPage />} />
                          <Route path="/auth/callback" element={<AuthCallback />} />
                          <Route path="/admin/login" element={<AdminLoginPage />} />
                          <Route path="/admin" element={<RequireAdmin><AdminDashboardPage /></RequireAdmin>} />
                          <Route path="*" element={<Home />} />
                        </Routes>
                        <CartDrawer />
                        <LiveChatGuard />
                      </CartProvider>
                    </CustomerAuthProvider>
                  </AdminAuthProvider>
                </HomeContentProvider>
              </SchemasProvider>
            </PricesProvider>
          </CurrencyProvider>
        </BrowserRouter>
      </HelmetProvider>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
